import { useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Banner, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { updateList, useListGroups, type EventTypeRef, type ListRow } from '../api';
import { ratio } from '../format';
import { Crumb, DialerPage, Eyebrow, ListSwitch, LoadError } from '../ui';
import { ActivateListDialog } from './ActivateListDialog';
import { CreateListDialog } from './CreateListDialog';
import { ImportListDialog } from './ImportListDialog';
import styles from './lists.module.css';

const ACTIVATE_CONFLICT = 'Bitte wählen Sie Setter und Ereignistyp.';
const EMPTY_PREFILL = { name: '', eventType: null };

/**
 * Terminierung 04 — Kontakte › Listen: Smart Views, manual lists, campaigns and import lists as cards with the same
 * table geometry; the „Dialer-Liste" switch makes a list diallable and asks for setters and event type when
 * the list has none.
 */
export function ListsPage() {
  const groups = useListGroups();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [importPrefill, setImportPrefill] = useState<{ name: string; eventType: EventTypeRef | null } | null>(
    null,
  );
  const [activating, setActivating] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function toggle(row: ListRow, on: boolean) {
    setError(null);
    if (on && (row.setterCount === 0 || row.eventType === null)) {
      setActivating(row.id);
      return;
    }
    setBusy(row.id);
    try {
      await updateList(row.id, { active: on });
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
    } catch (caught) {
      if (caught instanceof ApiError && caught.message === ACTIVATE_CONFLICT) setActivating(row.id);
      else setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <DialerPage crumb={<Crumb>Kontakte · Listen</Crumb>}>
      <div className={styles.listsPage}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Listen</h2>
            <p className={styles.lead}>Mit „Dialer-Liste“ geben Sie eine Liste zum Anrufen frei.</p>
          </div>
          <button type="button" className={styles.primary} onClick={() => setCreateOpen(true)}>
            Liste anlegen
          </button>
        </div>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {groups.isError ? (
          <LoadError
            error={groups.error}
            what="Die Listen"
            retrying={groups.isFetching}
            onRetry={() => void groups.refetch()}
          />
        ) : null}
        {groups.data
          ? groups.data.groups.map((group) => (
              <section key={group.type} className={styles.groupCard} aria-label={group.label}>
                <div className={styles.groupHead}>
                  <Eyebrow>{group.label}</Eyebrow>
                  <span className={styles.groupNote}>{group.note}</span>
                </div>
                <div className={styles.tableHead} aria-hidden="true">
                  <div>Liste</div>
                  <div>Wählbar / gesamt</div>
                  <div>Ereignistyp</div>
                  <div>Setter</div>
                  <div>Dialer-Liste</div>
                </div>
                {group.rows.length === 0 ? (
                  <div className={styles.emptyRow}>Noch keine Liste dieser Art.</div>
                ) : (
                  group.rows.map((row) => (
                    <div key={row.id} className={styles.tableRow}>
                      <div className={styles.cellName}>
                        <Link to={`/dialer/lists/${row.id}`} className={styles.rowLink}>
                          {row.name}
                        </Link>
                        <div className={styles.rowMeta}>{row.meta}</div>
                      </div>
                      <div className={styles.cellNumber}>
                        <span className={styles.mobileLabel}>Wählbar / gesamt </span>
                        {ratio(row.dialable, row.total)}
                      </div>
                      <div className={styles.cellText}>
                        <span className={styles.mobileLabel}>Ereignistyp </span>
                        {row.active && row.eventType ? row.eventType.name : '—'}
                      </div>
                      <div className={styles.cellText}>
                        <span className={styles.mobileLabel}>Setter </span>
                        {row.active && row.setterCount > 0 ? row.setterCount : '—'}
                      </div>
                      <div>
                        <ListSwitch
                          checked={row.active}
                          label={`Dialer-Liste: ${row.name}`}
                          busy={busy === row.id}
                          disabled={busy === row.id}
                          onChange={(on) => void toggle(row, on)}
                        />
                      </div>
                    </div>
                  ))
                )}
              </section>
            ))
          : groups.isPending
            ? Array.from({ length: 3 }, (_, index) => (
                <section key={index} className={styles.groupCard} aria-busy="true">
                  <div className={styles.groupHead}>
                    <Skeleton width={140} height={11} />
                  </div>
                  {Array.from({ length: 2 }, (_, row) => (
                    <div key={row} className={styles.tableRow}>
                      <div className={styles.cellName}>
                        <Skeleton width={220} height={14} />
                        <Skeleton width={260} height={12} />
                      </div>
                    </div>
                  ))}
                </section>
              ))
            : null}
      </div>
      <CreateListDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onImport={(prefill) => setImportPrefill(prefill)}
      />
      <ImportListDialog
        open={importPrefill !== null}
        prefill={importPrefill ?? EMPTY_PREFILL}
        onClose={() => setImportPrefill(null)}
      />
      <ActivateListDialog
        listId={activating}
        onClose={() => setActivating(null)}
        onDone={() => setActivating(null)}
      />
    </DialerPage>
  );
}
